import * as React from 'react';
import clsx from 'clsx';
import { Box, Button, Paper } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { BENEFITS_LOGOS } from '../../helpers/newDashboard';
import { renderHTMLString } from '../../helpers/helpers';
import { useTypedSelector } from '../../combineReducers';
import { useParams } from 'react-router-dom';
import { RouteParams } from 'aion-training-compliance-ui/src/features/operator/workGroupJobTypeContractor/containers/WorkGroupJobTypeContractorGeneralInfo';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      minHeight: 350,
      maxHeight: 350,
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2)
    },
    containerDisabled: {
      opacity: 0.5
    },
    logo: {
      width: '100%',
      padding: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    title: {
      color: '#1A1C1E',
      fontSize: 24,
      lineHeight: '24px',
      fontWeight: theme.typography.fontWeightBold,
      letterSpacing: 0.18,
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(1),
      textAlign: 'center'
    },
    subtitle: {
      color: 'var(--on-surface-high-emphasis-000000-87-)',
      fontSize: 14,
      lineHeight: '20px',
      fontWeight: theme.typography.fontWeightMedium,
      letterSpacing: 0.1,
      marginBottom: theme.spacing(3)
    },
    description: {
      color: 'var(--on-surface-high-emphasis-000000-87-)',
      fontSize: 14,
      maxWidth: '50%',
      lineHeight: '20px',
      fontWeight: theme.typography.fontWeightRegular,
      // marginBottom: theme.spacing(3),
      textAlign: 'center',
      margin: 'auto'
    },
    button: {
      letterSpacing: 1.25,
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      margin: 'auto',
      marginBottom: theme.spacing(1.5)
    },
    tel: {
      '& a': {
        color: 'var(--text-error)'
      }
    }
  })
);

export const TestLocators = {
  title: 'title',
  subtitle: 'subtitle',
  logo: 'logo',
  description: 'description',
  link: 'link'
};

type Props = {
  title: string;
  subtitle?: string;
  logo: string;
  description: string;
  id: string;
  buttons: {
    label: string;
    url: string;
    targetType: string;
  }[];
  type: string;
  disabled?: boolean;
};

export const Benefit: React.FC<Props> = ({ title, logo, description, buttons, type, disabled, id }) => {
  const classes = useStyles();
  const { credits: businessShieldCredits } = useTypedSelector(state => state.businessShieldCredits);
  const { SSOUrl: businessShieldSSOUrl } = useTypedSelector(state => state.businessShieldSSO);
  const { organization } = useTypedSelector(state => state.organization);
  const { organizationId } = useParams<RouteParams>();
  const Logo = BENEFITS_LOGOS[logo];

  const isPartnerLink = (link: string) => {
    return link.includes('partner');
  };

  const onLinkClick = (url: string, targetType: string) => () => {
    let link = url.replace('{organizationId}', organizationId);
    let target = targetType === 'internal' ? '_self' : '_blank';

    if (isPartnerLink(link) && organization !== null) {
      link = link.replace('{companyName}', organization.name);
    }

    if (type === 'businessShield' && businessShieldCredits === 1) {
      link = businessShieldSSOUrl || 'https://chas.business-shield.co.uk/chas/?page_id=9648';
    }
    window.open(link, target, 'noopener, noreferrer');
  };

  return (
    <Paper id={id} classes={{ root: clsx(classes.container, disabled && classes.containerDisabled) }}>
      <Box>
        <Box>{Logo ? <Logo className={classes.logo} data-testid={TestLocators.logo} /> : null}</Box>
        <Typography variant="h6" classes={{ root: classes.title }} data-testid={TestLocators.title}>
          {title}
        </Typography>

        <Typography
          variant="body1"
          classes={{ root: classes.description }}
          dangerouslySetInnerHTML={{
            __html: renderHTMLString(description)
          }}
          data-testid={TestLocators.description}
        />
      </Box>
      <Box display="flex">
        {buttons.map(({ label, url, targetType }, index) => (
          <Button
            key={index}
            color="primary"
            variant={disabled ? 'text' : index === 0 ? 'outlined' : 'outlined'}
            classes={{ root: classes.button }}
            onClick={onLinkClick(url, targetType || '')}
            disabled={disabled}
            data-testid={TestLocators.link}
          >
            {type === 'businessShield' && businessShieldCredits === 1 ? 'Log in' : label}
          </Button>
        ))}
      </Box>
    </Paper>
  );
};
